import React, { useCallback, useContext } from 'react';
import { useFormikContext } from 'formik';
import { observer } from 'mobx-react';
import { Grid } from '@material-ui/core';
import queryString from 'query-string';
import copy from 'copy-to-clipboard';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { preBindMVRStates, policyType } from '@ourbranch/lookups';

import { AuthContext } from 'core/components/auth';
import { withToast } from 'core/components/toast';
import { useStore } from 'core/store/store.mobx';

import useStyles from './footer.styles';

import OfferOutOfDate from './components/offer-out-of-date';
import NoOptionsAvailable from './components/no-options-available';
import UpdateMVRs from './components/update-MVRs';
import UpdateOffer from './components/update-offer';
import UWForm from './components/UW-form';
import Checkout from './components/checkout';

const Footer = observer(({ toast, showValidationErrorsModal, setShowValidationErrorsModal }) => {
  const classes = useStyles();
  const history = useHistory();
  const session = useContext(AuthContext);
  const { handleSubmit, dirty, values, validateForm, setErrors, setTouched } = useFormikContext();
  const { offer: store } = useStore();

  const agentIsLicensedForState = store.getIsLicensedForState(session);
  const { drivers } = store.offer.quote;
  const optionsAvailable = store?.offer?.options?.length > 0;

  const footerContainerClasses = classNames(classes.footerContainer, {
    [classes.footerAlert]: store.isStale || !optionsAvailable
  });

  const needToUpdateMVRs =
    !dirty && preBindMVRStates.includes(store.state) && drivers.some((driver) => driver.postBindMVR);

  const onCheckout = useCallback(async () => {
    const checkoutErrors = await validateForm();
    if (Object.keys(checkoutErrors).length > 0) {
      setErrors(checkoutErrors);
      setTouched(checkoutErrors);
      setShowValidationErrorsModal(true);
    } else {
      history.push(`/offer/${store.offer.id}/${values.selectedOption}/checkout`);
    }
  }, [history, setErrors, setTouched, store.offer.id, values, setShowValidationErrorsModal, validateForm]);

  const onContinueToUWForm = useCallback(async () => {
    const updateErrors = await validateForm();
    if (Object.keys(updateErrors).length > 0) {
      setErrors(updateErrors);
      setTouched(updateErrors);
      setShowValidationErrorsModal(true);
    } else {
      history.push(`/offer/${store.offer.id}/verifyUW`);
    }
  }, [history, setShowValidationErrorsModal, validateForm, setErrors, setTouched, store.offer.id]);

  const onUpdate = useCallback(async () => {
    const updateErrors = await validateForm();
    if (Object.keys(updateErrors).length > 0) {
      setErrors(updateErrors);
      setTouched(updateErrors);
      setShowValidationErrorsModal(true);
    } else if (needToUpdateMVRs) {
      await store.recalculateQuoteToCluster(
        store.offer.id,
        { getMVRs: true, selectedOption: values.selectedOption },
        history
      );
    } else {
      handleSubmit();
    }
  }, [
    handleSubmit,
    setShowValidationErrorsModal,
    validateForm,
    setErrors,
    setTouched,
    needToUpdateMVRs,
    history,
    store,
    values.selectedOption
  ]);

  const copyLink = () => {
    const { protocol, host, search } = window.location;
    const newHost = host.replace(/staff\./i, 'www.'); // staff.ourbranch.com -> www.ourbranch.com, for example

    const queryParamsMap = queryString.parse(search);
    const selectedOption = values.selectedOption || queryParamsMap.option;

    let policyType;
    if (selectedOption === 'HB' || selectedOption === 'AB') {
      // if we're giving a bundled price, link to the bundle
      policyType = 'HA';
    } else {
      policyType = selectedOption;
    }

    const link = store.offer.clusterId
      ? `${protocol}//${newHost}/review?cid=${store.offer.clusterId}&planType=${store.offer.code}&policyType=${policyType}&internal=true`
      : `${protocol}//${newHost}/quote/detail/${store.offer.id}`;
    const copied = copy(link);
    if (copied) {
      toast.notify({
        type: 'success',
        message: 'The link is copied to your clipboard. This link is for customer use ONLY.'
      });
    } else {
      toast.notify({
        type: 'error',
        message: `We could not copy the link, but here it is: ${link}`
      });
    }
  };

  // Agents cannot update or checkout offers for states they are not licensed in, so don't show footer
  if (!agentIsLicensedForState) {
    return null;
  }

  return (
    <div className={classes.floatingContainer}>
      <div className={footerContainerClasses}>
        <Grid container alignItems="center" className={classes.footerContent}>
          {(store.isStale && <OfferOutOfDate onUpdate={onUpdate} />) ||
            (!optionsAvailable && <NoOptionsAvailable />) ||
            (needToUpdateMVRs && <UpdateMVRs onUpdate={onUpdate} copyLink={copyLink} />) ||
            (dirty && <UpdateOffer onUpdate={onUpdate} />) ||
            (store.needsPreBindUWForm && values?.selectedOption?.includes(policyType.Home) && (
              <UWForm
                showValidationErrorsModal={showValidationErrorsModal}
                copyLink={copyLink}
                onContinueToUWForm={onContinueToUWForm}
              />
            )) ||
            (!dirty && (
              <Checkout
                showValidationErrorsModal={showValidationErrorsModal}
                copyLink={copyLink}
                onCheckout={onCheckout}
              />
            ))}
        </Grid>
      </div>
    </div>
  );
});

export default withToast(Footer);
